import {ReactNode} from 'react';
import {View, TouchableOpacity, StyleSheet, ViewStyle} from 'react-native';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {useCommonStyles} from '@/hooks/useCommonStyles';

interface BaseCardProps {
    children: ReactNode;
    onPress?: () => void;
    style?: ViewStyle;
    variant?: 'default' | 'compact' | 'large' | 'flex';
    disabled?: boolean;
    activeOpacity?: number;
    outlined?: boolean;
}

export const BaseCard = ({
    children,
    onPress,
    style,
    variant = 'default',
    disabled = false,
    activeOpacity = 0.7,
    outlined = false,
}: BaseCardProps) => {
    const {theme} = useCurrentTheme();
    const commonStyles = useCommonStyles();
    const styles = createStyles(theme);

    const cardStyle = [
        styles.baseCard,
        commonStyles.shadows.md,
        styles[variant],
        style,
        outlined && styles.outline,
    ];

    if (onPress && !disabled) {
        return (
            <TouchableOpacity
                style={cardStyle}
                onPress={onPress}
                activeOpacity={activeOpacity}
                disabled={disabled}
            >
                {children}
            </TouchableOpacity>
        );
    }

    return (
        <View style={cardStyle}>
            {children}
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    baseCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
    },

    outline: {
        borderWidth: 1,
        borderColor: theme.colors.border,
    },

    default: {
        marginBottom: theme.spacing.md,
        marginHorizontal: theme.spacing.xs,
    },

    compact: {
        marginBottom: theme.spacing.sm,
        marginHorizontal: theme.spacing.xs,
        borderRadius: theme.borderRadius.sm,
    },

    large: {
        marginBottom: theme.spacing.md,
        marginHorizontal: theme.spacing.lg,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
    },

    flex: {
        flex: 1,
        marginBottom: theme.spacing.md,
        marginHorizontal: theme.spacing.xs,
    },
});
